import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box,
  Typography, Alert, IconButton, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { api } from '../api/client.js';
import type { ISlackSendResult } from '../../shared/types.js';
import { colors } from '../styles/theme.js';

const MAX_SCREENSHOTS = 4;

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * Fehler melden aus der laufenden Oberflaeche. Geht als Slack-Nachricht in den
 * Entwickler-Channel (Kanal `bug_report`, Admin-Bereich → Slack Config).
 *
 * Screenshots kommen per Strg+V aus der Zwischenablage — kein Datei-Dialog, weil
 * der uebliche Weg "Snipping Tool → einfuegen" ist.
 */
export default function BugReportDialog({ open, onClose }: Props) {
  const location = useLocation();
  const [page, setPage] = useState('');
  const [description, setDescription] = useState('');
  const [screenshots, setScreenshots] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ISlackSendResult | null>(null);

  // Bei jedem Oeffnen frisch: Seite vorbelegen, alte Eingaben verwerfen.
  useEffect(() => {
    if (!open) return;
    setPage(location.pathname + location.search);
    setDescription('');
    setScreenshots([]);
    setError(null);
    setResult(null);
  }, [open, location.pathname, location.search]);

  const readImage = (file: File) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Bild konnte nicht gelesen werden'));
    reader.readAsDataURL(file);
  });

  const handlePaste = async (e: React.ClipboardEvent) => {
    const files = Array.from(e.clipboardData.items)
      .filter((i) => i.kind === 'file' && i.type.startsWith('image/'))
      .map((i) => i.getAsFile())
      .filter((f): f is File => f !== null);
    if (files.length === 0) return;

    e.preventDefault();
    const room = MAX_SCREENSHOTS - screenshots.length;
    if (room <= 0) {
      setError(`Maximal ${MAX_SCREENSHOTS} Screenshots.`);
      return;
    }
    try {
      const added = await Promise.all(files.slice(0, room).map(readImage));
      setScreenshots((s) => [...s, ...added]);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleSubmit = async () => {
    setSending(true);
    setError(null);
    const res = await api.post<ISlackSendResult>('/bug-report', { page, description, screenshots });
    setSending(false);
    if (res.success) setResult(res.data);
    else setError(res.error.message);
  };

  const canSubmit = description.trim().length > 0 && !sending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        Fehler melden
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {result ? (
          <Alert severity={result.sent ? 'success' : 'warning'}>
            {result.sent
              ? 'Danke — der Report ist im Slack-Channel gelandet.'
              : `Der Report konnte nicht zugestellt werden: ${result.reason}`}
          </Alert>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <TextField
              label="Auf welcher Seite ist es passiert?"
              size="small"
              fullWidth
              value={page}
              onChange={(e) => setPage(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              label="Was ist passiert?"
              multiline
              minRows={4}
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Was hast du gemacht, was war das Ergebnis, was hattest du erwartet?"
              sx={{ mb: 2 }}
            />

            <Box
              onPaste={handlePaste}
              tabIndex={0}
              sx={{
                border: `1px dashed ${colors.silver}`,
                borderRadius: 1,
                p: 2,
                textAlign: 'center',
                cursor: 'text',
                '&:focus': { borderColor: colors.lime, outline: 'none' },
              }}
            >
              <Typography variant="body2" sx={{ color: colors.black60 }}>
                Screenshot mit <strong>Strg&nbsp;+&nbsp;V</strong> hier einfügen
                {screenshots.length > 0 && ` (${screenshots.length}/${MAX_SCREENSHOTS})`}
              </Typography>
            </Box>

            {screenshots.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                {screenshots.map((src, i) => (
                  <Box key={i} sx={{ position: 'relative' }}>
                    <Box
                      component="img"
                      src={src}
                      alt={`Screenshot ${i + 1}`}
                      sx={{ width: 120, height: 80, objectFit: 'cover', border: `1px solid ${colors.silver}`, borderRadius: 1 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => setScreenshots((s) => s.filter((_, idx) => idx !== i))}
                      sx={{
                        position: 'absolute', top: 2, right: 2,
                        bgcolor: 'rgba(255,255,255,0.9)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" sx={{ color: colors.black60 }}>
          {result ? 'Schließen' : 'Abbrechen'}
        </Button>
        {!result && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit}
            startIcon={sending ? <CircularProgress size={16} /> : undefined}
            sx={{
              bgcolor: colors.lime, color: colors.black,
              '&:hover': { bgcolor: '#b8e600' }, '&.Mui-disabled': { bgcolor: colors.lightGrey },
            }}
          >
            Absenden
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
